"use client"

/**
 * The single desktop dialog primitive for the PWA — the canon's "dialog card"
 * (`.tondocs/ui/DIALOGS.md`): a solid dark `rounded-[34px]` panel whose body
 * scrolls under a transparent floating footer holding one primary CTA.
 *
 * It is the DESKTOP TWIN of `DockSheet` (`components/ui/dock-sheet.tsx`), and
 * that symmetry is load-bearing, not decoration: 35 of the 56 dialogs in this
 * repo render a `DockSheet` on the other side of a `useMobile()` branch, and
 * they drifted apart precisely because the sheet's chrome was declarative and
 * the dialog's was hand-rolled.
 *
 * Nothing else may render dialog chrome — pinned by
 * `tests/unit/fitness-dialog-chrome-in-primitive.test.ts`, which fails any new
 * `<DialogContent` in `components/**`. `DialogContent` paints no title, no
 * close button and no padding (its stock shadcn close was deleted years ago;
 * the dead `import { X }` it left on `dialog.tsx` was swept in step E), so 40
 * of 56 call sites drew their own close — 3 button sizes, 3 icon sizes, 2
 * incompatible positioning models, ≥8 header paddings — and 34 shipped the
 * `sr-only DialogTitle + hand-written <h2> + pr-10` pattern that let a long
 * title slide UNDER the absolutely-positioned close button. That is why the
 * chrome below is a CLOSED union rather than a `customHeader` slot — an open
 * slot is what manufactured the same drift on the sheet side.
 *
 * The chrome control geometry (36px button, 20px icon, `p-4` header) is the
 * canon from HANDOFF §6 Q1 and `DockSheet` was snapped to the same numbers in
 * the same change — "one size for the sheet and the dialog" is the decision,
 * so a divergence here is a regression, not a variant.
 *
 * @see .tondocs/ui/HANDOFF_DIALOG_CHROME_2026_08_01.md — this consolidation (§4 API, §6 decisions)
 * @see .tondocs/ui/DIALOGS.md         — dialog-card canon (tokens, nested, forbidden patterns)
 * @see .tondocs/ui/OVERLAY_FOOTER.md  — footer contract (transparent + gradient fade)
 * @see .tondocs/ui/SHEET_CANON.md     — cross-platform canon; `DockSheet` is the mobile twin
 * @see .tondocs/ui/MOBILE_INPUTS.md   — `text-base` on every editable field inside
 */

import React from "react"
import { useTranslations } from "next-intl"
import { ChevronLeft, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { unreachable } from "@/lib/type-helpers"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import {
  DIALOG_CARD_CONTENT_BASE,
  DIALOG_CARD_CONTENT_BASE_NESTED,
  DIALOG_CARD_OVERLAY_CLASS,
  DIALOG_CARD_OVERLAY_CLASS_NESTED,
} from "@/lib/dialog-styles"

interface DialogCardBaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  /**
   * Floating CTA dock — transparent, over a gradient fade, with the body
   * scrolling under it (`OVERLAY_FOOTER.md`). Never pass an opaque bar; the
   * slot bakes the whole contract in, which is the point of the prop.
   */
  footer?: React.ReactNode
  /** Stacked on top of an already-open dialog card → the `*_NESTED` z-layer. */
  nested?: boolean
  /**
   * Classes for the SCROLL BODY — mirrors `DockSheet.contentClassName`
   * (e.g. `"px-0 pt-0"` for an edge-to-edge body). Not the panel.
   */
  contentClassName?: string
  /**
   * Classes for the PANEL itself — width (`max-w-2xl`), a fixed height
   * (`h-[80dvh]`), etc. Split from `contentClassName` because the sheet twin
   * owns the same two surfaces separately and a single prop would silently
   * mean different things on the two branches of a responsive shell.
   */
  panelClassName?: string
  /**
   * Child owns the scroll region (e.g. a body shared with the sheet branch
   * that already carries its own scroller + floating dock). Mirror of
   * `DockSheet.scrollManagedByChild`: the slot degrades to a bare flex column
   * — no scroller of its own, no bottom reserve — instead of nesting a second
   * scroll container inside the child's.
   *
   * It replaces the `contentClassName="flex min-h-0 flex-col px-0 pt-0 pb-0!"`
   * incantation that four call sites had converged on independently; the `!`
   * in it was the tell that the primitive was being fought, not configured.
   */
  scrollManagedByChild?: boolean
  /**
   * Extra classes for the OVERLAY (the dimmer), merged over the standard /
   * nested token. Two real needs, both of which the panel cannot express:
   * dimming DOWN while a fullscreen child (chat gallery) is up, and hiding
   * the overlay at a breakpoint (`md:hidden`) on a mobile-only card.
   */
  overlayClassName?: string
  /**
   * Radix dismissal guards, passed straight through to `DialogContent`. A card
   * that portals a fullscreen child (gallery, nested report dialog) must not
   * treat a click inside that child as "outside".
   */
  onInteractOutside?: React.ComponentPropsWithoutRef<typeof DialogContent>["onInteractOutside"]
  onPointerDownOutside?: React.ComponentPropsWithoutRef<typeof DialogContent>["onPointerDownOutside"]
  /**
   * `data-*` markers set on the PANEL element. Deliberately narrower than a
   * `panelProps` spread: this exists for DOM markers that outer shells read
   * with `closest()` (`data-report-dialog`, which `ChatDialog` uses to stay
   * open while a report card is being used), and a general props bag would
   * quietly re-open `className` / handlers as an escape hatch beside the
   * closed chrome union. Put the marker on the panel, not on a body wrapper:
   * a click on the CHROME row must be covered by the guard too.
   */
  panelDataAttributes?: Record<`data-${string}`, string | number | boolean>
  /**
   * Disables the chrome close BUTTON (e.g. while a payment is in flight).
   * Escape and the backdrop stay live — same three-route asymmetry the
   * hand-rolled `disabled={isBusy}` X had before the migration. Locking all
   * three is a different contract and belongs in its own prop if ever needed.
   */
  closeDisabled?: boolean
}

/**
 * Which chrome the card paints. A closed union, not an escape hatch: the
 * fields of one variant are `never` on the others, so `chrome="none"` with a
 * `title` does not compile. Mirror of `DockSheetChrome`.
 *
 * Ordered by descending chrome:
 *
 * - `standard`  — title row + close (+ optional back, + `belowTitle`). The default.
 * - `titleOnly` — title row, NO close button. A deliberate choice for cards the
 *                 body dismisses itself (a flow step, a CTA-only decision), not
 *                 "I forgot the close button".
 * - `closeOnly` — close button, NO title. The mirror of `DockSheet`'s
 *                 `handleOnly`: the body opens with its own heading, so a chrome
 *                 title would be a second one. Step A found ≥6 real call sites
 *                 (`checkout/payment-sheet` desktop, `GlobalQuickCheckout`
 *                 `step="detail"`, `global-auction-bid-sheet` while loading, the
 *                 two duplicated "Moderation warning" dialogs,
 *                 `search/mobile-modal` ×2) that none of the other three cover.
 *                 Deliberately NOT modelled as `standard` with `title: ""` — an
 *                 empty-string escape hatch is exactly the kind of hole a closed
 *                 union exists to refuse.
 * - `none`      — no chrome at all; `children` carry their own UI (`command.tsx`,
 *                 chat dialogs).
 *
 * `a11yTitle` is REQUIRED on the two variants that paint no visible title
 * (`closeOnly`, `none`) and `never` on the two that do. Radix must have an
 * accessible name for `role="dialog"` either way — a close button is a control,
 * not a name. That is the one thing those variants cannot opt out of.
 */
type DialogCardChrome =
  | {
      chrome?: "standard"
      title: string
      onBack?: () => void
      titleAlign?: "left" | "center"
      belowTitle?: React.ReactNode
      a11yTitle?: never
    }
  | {
      chrome: "titleOnly"
      title: string
      onBack?: never
      titleAlign?: "left" | "center"
      belowTitle?: never
      a11yTitle?: never
    }
  | {
      chrome: "closeOnly"
      title?: never
      onBack?: never
      titleAlign?: never
      belowTitle?: never
      a11yTitle: string
    }
  | {
      chrome: "none"
      title?: never
      onBack?: never
      titleAlign?: never
      belowTitle?: never
      a11yTitle: string
    }

export type DialogCardProps = DialogCardBaseProps & DialogCardChrome

/* Chrome control geometry — HANDOFF §6 Q1: 36px button, 20px icon, `p-4`
   header. The colour pair is `DockSheet`'s on purpose, and it is now TOKENS,
   not literals — that consolidation is exactly what let Phase 2 tokenise one
   place per surface instead of forty. Byte-for-byte the same in dark:
   `bg-muted` `#262626` = the old `bg-neutral-800`, `bg-sheet-handle` `#404040`
   = `bg-neutral-700`, `text-muted-foreground` `#a3a3a3` = `text-neutral-400`.
   Do not reintroduce a literal here. */
const CHROME_BUTTON_CLASS =
  "w-9 h-9 flex items-center justify-center rounded-full bg-muted hover:bg-sheet-handle active:bg-sheet-handle transition-colors shrink-0"

const CHROME_ICON_CLASS = "w-5 h-5 text-muted-foreground"

/* `leading-7` / `tracking-normal` neutralise `DialogTitle`'s own
   `leading-none tracking-tight`: `truncate` sets `overflow:hidden`, and a
   zero-leading line clips descenders inside it. The resulting metrics are the
   default `text-lg` ones — i.e. exactly what the sheet twin's `<h2>` renders. */
const CHROME_TITLE_CLASS =
  "text-lg font-semibold leading-7 tracking-normal text-sheet-foreground truncate min-w-0"

export function DialogCard(props: DialogCardProps) {
  const {
    open,
    onOpenChange,
    children,
    footer,
    nested = false,
    contentClassName,
    panelClassName,
    scrollManagedByChild = false,
    overlayClassName,
    onInteractOutside,
    onPointerDownOutside,
    panelDataAttributes,
    closeDisabled = false,
  } = props

  /* The chrome controls are the only strings the primitive itself renders, and
     they are assistive-only — so they must still be translated. Before the
     migration each call site spelled `aria-label={tCommon("close")}` by hand;
     folding the chrome into the primitive without this would have silently
     shipped an English "Close" to nine locales. `common.close` / `common.back`
     already exist in all nine message files — no new keys. `DockSheet` reads
     the same two keys, so the twins cannot drift here either. */
  const tCommon = useTranslations("common")

  const closeButton = (
    <button
      type="button"
      onClick={() => onOpenChange(false)}
      disabled={closeDisabled}
      className={cn(CHROME_BUTTON_CLASS, "disabled:opacity-50 disabled:cursor-not-allowed")}
      aria-label={tCommon("close")}
    >
      <X className={CHROME_ICON_CLASS} strokeWidth={2.5} />
    </button>
  )

  const renderChrome = () => {
    switch (props.chrome) {
      case "none":
        return <DialogTitle className="sr-only">{props.a11yTitle}</DialogTitle>

      case "closeOnly":
        return (
          <>
            <DialogTitle className="sr-only">{props.a11yTitle}</DialogTitle>
            {/* Same row box as `standard` (`p-4`, same 36px control), so a
                surface that swaps between the two — `GlobalQuickCheckout` does
                it per `step` — does not shift its body by a pixel. */}
            <div className="shrink-0 flex items-center justify-end p-4">{closeButton}</div>
          </>
        )

      case "titleOnly": {
        const { title, titleAlign = "center" } = props
        return (
          <div className="shrink-0 p-4">
            <DialogTitle
              className={cn(CHROME_TITLE_CLASS, titleAlign === "center" && "text-center")}
            >
              {title}
            </DialogTitle>
          </div>
        )
      }

      case undefined:
      case "standard": {
        const { title, onBack, titleAlign = "center", belowTitle } = props
        return (
          <div className="shrink-0">
            {/* The close button is IN FLOW — a flex sibling of the title, not
                `absolute` (HANDOFF §6 Q2). Absolute is what forced the `pr-10`
                compensation on 10 files and still let long titles slide under
                the button; here `flex-1 truncate` next to a `shrink-0` control
                makes that geometrically impossible. The left-hand spacer is
                load-bearing for `titleAlign="center"`: it mirrors the 36px
                close so the title centres on the FREE space, not on the whole
                container (the other half of the same scar). */}
            <div className={cn("flex items-center gap-3 p-4", belowTitle && "pb-3")}>
              {onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className={CHROME_BUTTON_CLASS}
                  aria-label={tCommon("back")}
                >
                  <ChevronLeft className={CHROME_ICON_CLASS} strokeWidth={2.5} />
                </button>
              ) : titleAlign === "center" ? (
                <div className="w-9 h-9 shrink-0" aria-hidden />
              ) : null}
              <DialogTitle
                className={cn(
                  CHROME_TITLE_CLASS,
                  "flex-1",
                  titleAlign === "center" && "text-center",
                )}
              >
                {title}
              </DialogTitle>
              {closeButton}
            </div>
            {belowTitle ? <div className="shrink-0 px-4 pb-4">{belowTitle}</div> : null}
          </div>
        )
      }

      default:
        return unreachable(props)
    }
  }

  /* How much room the scroll body keeps free at the bottom, as an overridable
     class (see the note at the body). Four cases, in precedence order:

     1. `scrollManagedByChild` — the child owns the scroller AND its own dock,
        so a reserve here would double-count. Safe area only. (`DockSheet`
        makes the identical call.)
     2. `footer` — the floating CTA sits over the body; 6rem clears one
        `h-12` button plus the gradient.
     3. `chrome="none"` — the card is a bare frame around children that carry
        the entire surface. It has no chrome to be symmetric with, so a
        breathing-room reserve is the primitive editorialising about a layout
        it does not own. Safe area only.
     4. otherwise — 1.5rem of breathing room under the last element, matching
        the 1rem side/top padding. */
  const bodyBottomReserve =
    scrollManagedByChild || (props.chrome === "none" && !footer)
      ? "pb-[env(safe-area-inset-bottom)]"
      : footer
        ? "pb-[calc(6rem_+_env(safe-area-inset-bottom))]"
        : "pb-[calc(1.5rem_+_env(safe-area-inset-bottom))]"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        noTransform
        overlayClassName={cn(
          nested ? DIALOG_CARD_OVERLAY_CLASS_NESTED : DIALOG_CARD_OVERLAY_CLASS,
          overlayClassName,
        )}
        className={cn(
          nested ? DIALOG_CARD_CONTENT_BASE_NESTED : DIALOG_CARD_CONTENT_BASE,
          "flex flex-col",
          panelClassName,
        )}
        onInteractOutside={onInteractOutside}
        onPointerDownOutside={onPointerDownOutside}
        {...panelDataAttributes}
      >
        {renderChrome()}
        {/* `grow` (flex-basis stays `auto`), NOT `flex-1` (basis 0). With
            basis 0 + `min-h-0` inside an auto-height panel the body collapses
            to nothing — the same class of scar as `DockSheet`'s hug branch.
            `grow` + `min-h-0` gives BOTH behaviours from one branch: the panel
            hugs short content, and once it hits `max-h-[calc(100dvh-2rem)]`
            (from `DialogContent`) the body shrinks and scrolls instead.

            The bottom reserve is a CLASS, not an inline style: inline padding
            wins over every utility, so `contentClassName` could only fight it
            with `pb-0!` — which is exactly what four call sites ended up
            writing. As a class it goes through tailwind-merge and a consumer
            that needs a different reserve (a two-button footer, say) just
            passes `pb-[…]` and wins. */}
        <div
          className={cn(
            "grow min-h-0 overflow-x-hidden px-4 pt-4",
            scrollManagedByChild
              ? "flex min-h-0 flex-col overflow-hidden"
              : "overflow-y-auto scrollbar-hide",
            bodyBottomReserve,
            contentClassName,
          )}
        >
          {children}
        </div>
        {footer ? (
          <>
            <div
              className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-20 bg-linear-to-t from-sheet to-transparent"
              aria-hidden
            />
            <div
              className="absolute bottom-0 left-0 right-0 z-20 px-4 pt-4"
              style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
            >
              {footer}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
